import { useState, useEffect, useCallback } from 'react'
import {
  generateAllQuestions,
  buildOptions,
  checkArabicInput,
  DIFFICULTY_CHOICES,
  type Difficulty,
  type RomanQuestion,
} from './romanNumbersLogic'

export type { Difficulty }

export interface RomanGameState {
  questions:      RomanQuestion[]
  currentIdx:     number
  options:        RomanQuestion[]   // MCQ choices (easy/medium)
  eliminated:     Set<number>       // indices of wrong MCQ options
  answered:       number
  moves:          number
  correctFlashId: number | null     // arabic value of just-selected correct option
  startTime:      number | null
  endTime:        number | null
  gameComplete:   boolean
  // hard mode
  inputValue:     string
  inputStatus:    'idle' | 'correct' | 'wrong'
}

function initState(difficulty: Difficulty): RomanGameState {
  const questions = generateAllQuestions(difficulty)
  const choices   = DIFFICULTY_CHOICES[difficulty]
  const options   = difficulty !== 'hard' && questions.length > 0
    ? buildOptions(questions[0], choices, difficulty)
    : []
  return {
    questions,
    currentIdx:     0,
    options,
    eliminated:     new Set(),
    answered:       0,
    moves:          0,
    correctFlashId: null,
    startTime:      null,
    endTime:        null,
    gameComplete:   false,
    inputValue:     '',
    inputStatus:    'idle',
  }
}

export function useRomanNumbers(difficulty: Difficulty) {
  const [state, setState] = useState<RomanGameState>(() => initState(difficulty))

  const resetGame = useCallback(() => {
    setState(initState(difficulty))
  }, [difficulty])

  // Advance to next question after correct-answer flash
  useEffect(() => {
    if (state.correctFlashId === null) return
    const timer = setTimeout(() => {
      setState((prev) => {
        const nextIdx = prev.currentIdx + 1
        if (nextIdx >= prev.questions.length) {
          return { ...prev, correctFlashId: null, gameComplete: true, endTime: Date.now() }
        }
        const choices = DIFFICULTY_CHOICES[difficulty]
        const nextQ   = prev.questions[nextIdx]
        const options = difficulty !== 'hard' ? buildOptions(nextQ, choices, difficulty) : []
        return {
          ...prev,
          currentIdx:     nextIdx,
          options,
          eliminated:     new Set(),
          correctFlashId: null,
          inputValue:     '',
          inputStatus:    'idle',
        }
      })
    }, 700)
    return () => clearTimeout(timer)
  }, [state.correctFlashId, difficulty])

  // Clear wrong-input shake after animation
  useEffect(() => {
    if (state.inputStatus !== 'wrong') return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, inputStatus: 'idle' }))
    }, 600)
    return () => clearTimeout(timer)
  }, [state.inputStatus])

  const selectOption = useCallback((optionArabic: number) => {
    setState((prev) => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const current   = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (optionArabic === current.arabic) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: optionArabic }
      }
      const eliminated = new Set(prev.eliminated)
      eliminated.add(optionArabic)
      return { ...prev, moves, startTime, eliminated }
    })
  }, [])

  const setInputValue = useCallback((val: string) => {
    setState((prev) => ({ ...prev, inputValue: val, inputStatus: 'idle' }))
  }, [])

  const submitInput = useCallback(() => {
    setState((prev) => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const current   = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (checkArabicInput(prev.inputValue, current)) {
        return {
          ...prev, moves, startTime,
          answered:       prev.answered + 1,
          inputStatus:    'correct',
          correctFlashId: current.arabic,
        }
      }
      return { ...prev, moves, startTime, inputStatus: 'wrong' }
    })
  }, [])

  return { state, resetGame, selectOption, setInputValue, submitInput }
}
